import {
  ref,
  shallowRef,
  computed,
  defineComponent,
  inject,
  provide,
  onMounted,
  onUnmounted,
  h,
  type InjectionKey,
  type PropType,
  type Slot
} from 'vue';

import { type OAuth2Tokens, type OAuth2TokenStorage, type RestApiClient } from '@docyrus/api-client';

import {
  type AuthMode,
  type AuthStatus,
  type DocyrusAuthConfig,
  type DocyrusCalendarEventPayload,
  type DocyrusEmailPayload,
  type DocyrusSharePayload,
  type HostNavigationHandler,
  type HostNavigationRequestOptions,
  type HostNotificationHandler,
  type RouteChangePayload
} from '../types';

import { AuthManager } from '../core/auth-manager';
import { type MsGraphClient } from '../core/ms-graph-client';

// ─── Internal reactive auth state ────────────────────────────────

interface DocyrusAuthState {
  status: AuthStatus;
  mode: AuthMode | null;
  tokens: OAuth2Tokens | null;
  client: RestApiClient | null;
  error: Error | null;
  signIn: () => void;
  signInWithTokens: (tokens: OAuth2Tokens) => Promise<void>;
  getAuthorizationUrl: () => Promise<string | null>;
  signOut: () => Promise<void>;
  onHostNavigation: (handler: HostNavigationHandler) => () => void;
  onHostNotification: (handler: HostNotificationHandler) => () => void;
  enableHostRouteSync: () => void;
  notifyRouteChange: (payload?: RouteChangePayload) => void;
  requestHostNavigation: (url: string, options?: HostNavigationRequestOptions) => void;
  sendToDocy: (payload: DocyrusSharePayload) => Promise<void>;
  sendToChat: (payload: DocyrusSharePayload) => Promise<void>;
  sendToEmail: (payload: DocyrusEmailPayload) => Promise<void>;
  sendToCalendar: (payload: DocyrusCalendarEventPayload) => Promise<void>;
  msGraph: MsGraphClient | null;
}

const DOCYRUS_AUTH_KEY: InjectionKey<DocyrusAuthState> = Symbol('docyrus-auth');

// ─── Composables ─────────────────────────────────────────────────

/**
 * Vue composable to access Docyrus auth state.
 * Must be used within a `<DocyrusAuthProvider>`.
 *
 * Returns: { status, mode, tokens, signIn, signInWithTokens, signOut, client, error }
 */
export function getDocyrusAuth(): DocyrusAuthState {
  const context = inject(DOCYRUS_AUTH_KEY);

  if (!context) {
    throw new Error(
      'getDocyrusAuth() must be used within a <DocyrusAuthProvider>. '
      + 'Wrap your app with <DocyrusAuthProvider> to provide auth context.'
    );
  }

  return context;
}

/** @deprecated Use `getDocyrusAuth()` instead. */
export const useDocyrusAuth = getDocyrusAuth;

/**
 * Vue composable to get the pre-configured RestApiClient.
 * Returns a computed ref that is null when not authenticated.
 */
export function getDocyrusClient() {
  const auth = getDocyrusAuth();

  return computed(() => auth.client);
}

/** @deprecated Use `getDocyrusClient()` instead. */
export const useDocyrusClient = getDocyrusClient;

// ─── Components ──────────────────────────────────────────────────

/**
 * Vue provider component that wraps your app with Docyrus auth context.
 *
 * Usage:
 * ```vue
 * <template>
 *   <DocyrusAuthProvider :apiUrl="apiUrl">
 *     <App />
 *   </DocyrusAuthProvider>
 * </template>
 *
 * <script setup>
 * import { DocyrusAuthProvider } from '@docyrus/signin/vue';
 * </script>
 * ```
 */
export const DocyrusAuthProvider = defineComponent({
  name: 'DocyrusAuthProvider',
  props: {
    clientId: { type: String, default: undefined },
    apiUrl: { type: String, default: undefined },
    scopes: { type: Array as PropType<string[]>, default: undefined },
    redirectUri: { type: String, default: undefined },
    callbackPath: { type: String, default: undefined },
    allowedHostOrigins: { type: Array as PropType<string[]>, default: undefined },
    storageKeyPrefix: { type: String, default: undefined },
    tokenStorage: { type: Object as PropType<OAuth2TokenStorage>, default: undefined },
    forceMode: { type: String as PropType<AuthMode>, default: undefined },
    initialTokens: { type: Object as PropType<OAuth2Tokens>, default: undefined },
    syncRouteToHost: { type: Boolean, default: false }
  },
  setup(props, { slots }) {
    const status = ref<AuthStatus>('loading');
    const mode = ref<AuthMode | null>(null);
    const tokens = shallowRef<OAuth2Tokens | null>(null);
    const client = shallowRef<RestApiClient | null>(null);
    const error = shallowRef<Error | null>(null);

    let manager: AuthManager | null = null;

    const signIn = () => {
      manager?.signIn();
    };

    const signInWithTokens = async (seedTokens: OAuth2Tokens) => {
      await manager?.signInWithTokens(seedTokens);
    };

    const signOut = async () => {
      await manager?.signOut();
    };

    const getAuthorizationUrl = async () => {
      return manager?.getAuthorizationUrl() ?? null;
    };

    const onHostNavigation = (handler: HostNavigationHandler) => {
      return manager?.onHostNavigation(handler) ?? (() => {});
    };

    const onHostNotification = (handler: HostNotificationHandler) => {
      return manager?.onHostNotification(handler) ?? (() => {});
    };

    const enableHostRouteSync = () => {
      manager?.enableHostRouteSync();
    };

    const notifyRouteChange = (payload?: RouteChangePayload) => {
      manager?.notifyHostRouteChange(payload);
    };

    const requestHostNavigation = (url: string, options?: HostNavigationRequestOptions) => {
      manager?.requestHostNavigation(url, options);
    };

    const sendToDocy = (payload: DocyrusSharePayload) => {
      return manager?.sendToDocy(payload)
        ?? Promise.reject(new Error('Auth manager not initialized'));
    };

    const sendToChat = (payload: DocyrusSharePayload) => {
      return manager?.sendToChat(payload)
        ?? Promise.reject(new Error('Auth manager not initialized'));
    };

    const sendToEmail = (payload: DocyrusEmailPayload) => {
      return manager?.sendToEmail(payload)
        ?? Promise.reject(new Error('Auth manager not initialized'));
    };

    const sendToCalendar = (payload: DocyrusCalendarEventPayload) => {
      return manager?.sendToCalendar(payload)
        ?? Promise.reject(new Error('Auth manager not initialized'));
    };

    const authState: DocyrusAuthState = {
      get status() { return status.value; },
      get mode() { return mode.value; },
      get tokens() { return tokens.value; },
      get client() { return client.value; },
      get error() { return error.value; },
      get msGraph() { return manager?.getMsGraphClient() ?? null; },
      signIn,
      signInWithTokens,
      getAuthorizationUrl,
      signOut,
      onHostNavigation,
      onHostNotification,
      enableHostRouteSync,
      notifyRouteChange,
      requestHostNavigation,
      sendToDocy,
      sendToChat,
      sendToEmail,
      sendToCalendar
    };

    provide(DOCYRUS_AUTH_KEY, authState);

    onMounted(() => {
      const config: DocyrusAuthConfig = {
        clientId: props.clientId,
        apiUrl: props.apiUrl,
        scopes: props.scopes,
        redirectUri: props.redirectUri,
        callbackPath: props.callbackPath,
        allowedHostOrigins: props.allowedHostOrigins,
        storageKeyPrefix: props.storageKeyPrefix,
        tokenStorage: props.tokenStorage,
        forceMode: props.forceMode,
        initialTokens: props.initialTokens,
        syncRouteToHost: props.syncRouteToHost
      };

      manager = new AuthManager(config);
      mode.value = manager.getMode();

      manager.subscribe((state) => {
        status.value = state.status;
        tokens.value = state.tokens;
        error.value = state.error;
        client.value = manager?.getClient() ?? null;
      });

      manager.initialize();
    });

    onUnmounted(() => {
      manager?.destroy();
      manager = null;
    });

    return () => slots.default?.();
  }
});

const ICON_SIZE = 20;

const LOGO_PATH = 'M768.11,531.68l-.24.21v101.17c0,26.8-21.79,48.63-48.59,48.63h-101.17c-8.72,0-16.82-2.33-23.88-6.35l-113.69,113.73c7.41,11.02,11.77,24.33,11.77,38.47,0,18.98-7.62,36.17-20.04,48.63-12.42,12.42-29.65,20.04-48.63,20.04s-36.2-7.62-48.63-20.04c-12.42-12.46-20.04-29.65-20.04-48.63,0-37.96,30.75-68.91,68.87-68.91,14.48,0,27.76,4.43,38.78,11.94l113.62-113.62c-4.19-7.1-6.55-15.31-6.55-24.09v-101.17c0-11.12,3.81-21.48,10.19-29.68l-93.72-127.52h-229.96V127.7h246.57v225.88l97.6,132.84c5.56-2.23,11.63-3.36,17.95-3.36h101.17c26.8,0,48.63,21.83,48.63,48.63Z';

function renderDocyrusLogo(size = ICON_SIZE) {
  return h('svg', {
    xmlns: 'http://www.w3.org/2000/svg',
    viewBox: '0 0 1024 1024',
    width: size,
    height: size,
    'aria-hidden': 'true',
    style: { display: 'block', flexShrink: 0 }
  }, [h('path', { fill: '#dc2626', d: LOGO_PATH })]);
}

function renderSpinner(size = ICON_SIZE) {
  return h('svg', {
    xmlns: 'http://www.w3.org/2000/svg',
    viewBox: '0 0 24 24',
    width: size,
    height: size,
    fill: 'none',
    'aria-hidden': 'true',
    style: { display: 'block', flexShrink: 0, animation: 'docyrus-spin 1s linear infinite' }
  }, [
    h('circle', {
      cx: 12, cy: 12, r: 10, stroke: 'currentColor', 'stroke-width': 3, opacity: 0.25
    }),
    h('path', {
      d: 'M12 2a10 10 0 0 1 10 10', stroke: 'currentColor', 'stroke-width': 3, 'stroke-linecap': 'round'
    }),
    h('style', {}, '@keyframes docyrus-spin{to{transform:rotate(360deg)}}')
  ]);
}

/**
 * "Sign in with Docyrus" button component.
 *
 * In iframe mode: renders nothing (auth is handled by the host).
 * When authenticated: renders nothing.
 *
 * Default look: [Logo] label — or — [Spinner] label (when loading).
 * Use class or the scoped slot for full customization.
 *
 * Usage:
 * ```vue
 * <SignInButton />
 * <SignInButton label="Login" class="btn-primary" />
 * <SignInButton v-slot="{ signIn, isLoading }">
 *   <MyCustomButton @click="signIn" :loading="isLoading" />
 * </SignInButton>
 * ```
 */
export const SignInButton = defineComponent({
  name: 'SignInButton',
  props: {
    label: { type: String, default: 'Sign in with Docyrus' },
    disabled: { type: Boolean, default: false }
  },
  setup(props, { slots, attrs }) {
    const auth = inject(DOCYRUS_AUTH_KEY);

    if (!auth) {
      throw new Error('SignInButton must be used within a <DocyrusAuthProvider>.');
    }

    const { signIn, status, mode } = auth;

    return () => {
      const isLoading = status === 'loading';

      // In iframe mode, the host handles sign-in
      if (mode === 'iframe') return null;

      // Already authenticated
      if (status === 'authenticated') return null;

      // Scoped slot for custom rendering
      if (slots.default) {
        return (slots.default as Slot)({ signIn, isLoading, status });
      }

      return h(
        'button',
        {
          type: 'button',
          onClick: signIn,
          disabled: props.disabled || isLoading,
          style: {
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
          },
          ...attrs
        },
        [isLoading ? renderSpinner() : renderDocyrusLogo(), h('span', {}, props.label)]
      );
    };
  }
});

// ─── Type re-exports ─────────────────────────────────────────────

export type { MsGraphClient, MsGraphRequestOptions } from '../core/ms-graph-client';
export type {
  AdaptiveCard,
  DocyrusSharePayload,
  DocyrusShareTextPayload,
  DocyrusShareAdaptiveCardPayload,
  DocyrusEmailPayload,
  DocyrusCalendarEventPayload,
  MsGraphRequest,
  MsGraphResponse,
  MsGraphAccount,
  MsGraphStatus
} from '../types';

export type {
  DocyrusAuthState, DocyrusAuthConfig, AuthMode, AuthStatus
};
